import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Survey } from '../../api/services/surveys/types.ts'
import SurveyReportTable from '../../components/application/surveyReport/SurveyReportTable.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import { useSurveyTemplates } from '../../hooks/useSurveyTemplates.ts'
import SurveyReportPDF_1 from '../../utils/assignmentCodes/PDFs/surveyReportPDF_1'
import SurveyReportPDF_2 from '../../utils/assignmentCodes/PDFs/surveyReportPDF_2'
import { previewBlob, toBase64Image } from '../../utils/pdf/pdfHelpers.ts'

const SurveyTemplateReport = () => {
  const { surveyTemplateID, assignmentCodeId } = useParams<{
    surveyTemplateID: string
    assignmentCodeId: string
  }>()
  const { loading, error, surveyReport, scoringRanges, getReport } =
    useSurveyTemplates()

  const [loadingPDF, setLoadingPDF] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [scoringRangeImages, setScoringRangeImages] = useState<
    Record<number, string>
  >({})

  const location = useLocation()
  const codeAssignment: string = location.state?.codeAssignment
  const mainCodeId: number = location.state?.mainCodeId
  const survey: Survey = location.state?.survey

  const navigate = useNavigate()

  useEffect(() => {
    if (assignmentCodeId === undefined) {
      navigate('/application/assignment-codes')
    }
  }, [assignmentCodeId])

  useEffect(() => {
    if (assignmentCodeId && surveyTemplateID === undefined) {
      navigate(`/application/assignment-codes/${assignmentCodeId}`)
    }
  }, [surveyTemplateID, assignmentCodeId])

  useLayoutEffect(() => {
    getReport(Number(assignmentCodeId), Number(surveyTemplateID))
  }, [])

  useEffect(() => {
    const loadImages = async () => {
      setImagesLoaded(false)

      const ranges = surveyReport?.scoring_ranges ?? []
      const entries = await Promise.all(
        ranges.map(async (range, index) => {
          if (!range.scoring_range_image) return null

          const base64 = await toBase64Image(range.scoring_range_image)
          return [index, base64] as const
        })
      )

      setScoringRangeImages(
        Object.fromEntries(
          entries.filter(Boolean) as Array<readonly [number, string]>
        )
      )
      setImagesLoaded(true)
    }

    loadImages().catch((error) => {
      console.error(error)
      setImagesLoaded(true)
    })
  }, [surveyReport?.scoring_ranges])

  const onClickReport = () => {
    switch (surveyTemplateID) {
      case '1':
        previewBlob(
          <SurveyReportPDF_1
            data={surveyReport}
            scoringRangeImages={scoringRangeImages}
          />,
          setLoadingPDF
        )
        break

      case '3':
        previewBlob(<SurveyReportPDF_2 data={surveyReport} />, setLoadingPDF)
        break
    }
  }

  if (error) {
    navigate(`/application/assignment-codes/${assignmentCodeId}/`, {
      state: { mainCodeId }
    })
  }

  return (
    <div>
      <Breadcrumbs
        pageName={`Szczegóły raportu dla kodu - ${codeAssignment}`}
        breadcrumbs={[
          { label: 'Kody przypisujące', path: '/application/assignment-codes' },
          {
            label: 'Szczegóły kodu',
            path: `/application/assignment-codes/${assignmentCodeId}/`,
            state: { mainCodeId }
          },
          { label: 'Raport' }
        ]}
      />

      <div className="flex w-full 2xl:flex-row flex-col gap-4">
        <div className="flex w-full flex-col gap-4">
          {loading && <Loader label={'Pobieranie danych...'} />}

          <Card className="w-full">
            {!error &&
              surveyReport &&
              surveyReport.scoring_ranges.length > 0 &&
              scoringRanges && (
                <React.Fragment>
                  <Card.Title className={'justify-between gap-4'}>
                    <span>{survey?.title}</span>

                    {surveyReport.total_patients_count > 0 && (
                      <Button
                        variant="filled"
                        size="medium"
                        color="primary"
                        type="button"
                        className={'my-4 md:my-0 md:mx-4'}
                        onClick={() => onClickReport()}
                        disabled={loadingPDF && !imagesLoaded}
                      >
                        {loadingPDF
                          ? 'Generowanie raportu....'
                          : 'Wygeneruj Raport'}
                      </Button>
                    )}

                    <div
                      className={
                        'flex flex-col gap-4 border mt-4 p-4 bg-gray-100 dark:bg-gray-800 dark:border-gray-700'
                      }
                    >
                      <p className={'text-sm'}>
                        Liczba osób biorących udział:
                        <span className={'ml-4 text-[18px] font-bold'}>
                          {surveyReport.total_patients_count}
                        </span>
                      </p>
                      <p className={'text-sm'}>
                        Średni wynik badanej grupy:
                        <span className={'ml-4 text-[18px] font-bold'}>
                          {surveyReport.average_score.toFixed(1)}
                        </span>
                        <span className={'ml-1 text-[16px]'}>
                          {' / '}
                          {surveyReport.scoring_ranges[2].max_score} pkt
                        </span>
                      </p>
                    </div>
                  </Card.Title>
                </React.Fragment>
              )}
          </Card>

          <Card className="w-full">
            {surveyReport &&
              surveyReport.scoring_ranges.length > 0 &&
              scoringRanges && (
                <React.Fragment>
                  <Card.Title>
                    {surveyReport.scoring_ranges[2].title}
                    <span
                      className={'px-2 text-sm'}
                    >{`( ${surveyReport.scoring_ranges[2].min_score}–${surveyReport.scoring_ranges[2].max_score} pkt )`}</span>
                  </Card.Title>
                  <Card.Body>
                    <p className={'p-4'}>
                      {surveyReport.scoring_ranges[2].description}
                    </p>
                    <SurveyReportTable
                      surveyReport={surveyReport.scoring_ranges[2].patients}
                      scoringRanges={scoringRanges}
                    />
                  </Card.Body>
                </React.Fragment>
              )}
          </Card>

          <Card className="w-full">
            {surveyReport &&
              surveyReport.scoring_ranges.length > 0 &&
              scoringRanges && (
                <React.Fragment>
                  <Card.Title>
                    {surveyReport.scoring_ranges[1].title}
                    <span
                      className={'px-2 text-sm'}
                    >{`( ${surveyReport.scoring_ranges[1].min_score}–${surveyReport.scoring_ranges[1].max_score} pkt )`}</span>
                  </Card.Title>
                  <Card.Body>
                    <p className={'p-4'}>
                      {surveyReport.scoring_ranges[1].description}
                    </p>
                    <SurveyReportTable
                      surveyReport={surveyReport.scoring_ranges[1].patients}
                      scoringRanges={scoringRanges}
                    />
                  </Card.Body>
                </React.Fragment>
              )}
          </Card>

          <Card className="w-full">
            {surveyReport &&
              surveyReport.scoring_ranges.length > 0 &&
              scoringRanges && (
                <React.Fragment>
                  <Card.Title>
                    {surveyReport.scoring_ranges[0].title}
                    <span
                      className={'px-2 text-sm'}
                    >{`( ${surveyReport.scoring_ranges[0].min_score}–${surveyReport.scoring_ranges[0].max_score} pkt )`}</span>
                  </Card.Title>
                  <Card.Body>
                    <p className={'p-4'}>
                      {surveyReport.scoring_ranges[0].description}
                    </p>
                    <SurveyReportTable
                      surveyReport={surveyReport.scoring_ranges[0].patients}
                      scoringRanges={scoringRanges}
                    />
                  </Card.Body>
                </React.Fragment>
              )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SurveyTemplateReport
